(function () {
  // ───────────────────────────────────────────────────────────────────────────
  // WHY THIS LISTENS ON `document` INSTEAD OF ON THE BLOCK
  //
  // The first version of this file bound its listeners directly to the buttons
  // and shipped a <script> tag inside the cart-drawer section. It worked on a
  // fresh page load and died the moment anybody used the cart, which is the
  // worst possible failure: it looked correct in testing.
  //
  // The theme repaints the drawer after every add by fetching the section and
  // swapping the markup in. A <script> tag that arrives through innerHTML or
  // DOMParser is INERT — the browser never executes it. So the replacement
  // drawer carried a script tag that had never run: no listeners, so the
  // buttons did nothing, and no lookup, so the block still read "your
  // organization". Measured directly: org "asjdfnlabdlaa" before the swap,
  // "your organization" after, with the tag sitting in the DOM the whole time.
  //
  // Hence: loaded once from layout/theme.liquid, clicks handled by delegation
  // on document, and an observer that names the organisation on whatever block
  // happens to be on the page. Any number of repaints, in any order.
  // ───────────────────────────────────────────────────────────────────────────

  // The portal's FORM_INGRESS_SECRET. Public by construction — this is a CDN
  // asset — so it deters casual posting and is not authentication.
  var SECRET = 'IgZLl07ySTDdoCsWnmg3WeBOORPqujuFoqy5UUOBtMc=';
  var API = 'https://partners.lovepaks.com/api/donation/variant';

  var busy = false;
  var orgCache = {};

  function blockOf(el) {
    return el ? el.closest('[data-lol-give]') : null;
  }
  function fail(root, msg) {
    var box = root && root.querySelector('[data-lol-error]');
    if (!box) return;
    box.textContent = msg;
    box.hidden = false;
  }
  function clearFail(root) {
    var box = root && root.querySelector('[data-lol-error]');
    if (box) box.hidden = true;
  }
  function lock(root, on) {
    busy = on;
    if (!root) return;
    Array.prototype.forEach.call(root.querySelectorAll('button'), function (b) {
      b.disabled = on;
    });
  }

  /* ── Naming the organisation ─────────────────────────────────────────────
     Until this resolves the block says "your organization", which is true but
     weak — the real name is most of why somebody gives. Cached per code so a
     drawer repainted five times asks once. */

  /* ── Warming the amount while it is still being typed ────────────────────
     A brand-new variant needs a few seconds before the storefront will sell
     it, and measured cold that cost 14 seconds of a button reading "Adding…".
     Nobody waits that long; they press it again, or leave.

     So the variant is minted on the way in rather than on the way out. Typing
     "287.19" fires a debounced request at the portal, and by the time a hand
     has travelled to the Add button the propagation window has usually already
     passed. The retry below stays as the safety net for whoever types fast.

     Nothing is charged by warming — a variant is a price, not an order. The
     worst case is an unused variant, and unused amounts can be pruned. */
  var warmed = {};
  var warmTimer = null;

  function warm(root, raw) {
    var amount = String(raw || '').trim();
    if (!/^\d{1,5}(\.\d{1,2})?$/.test(amount)) return;
    var n = Number(amount);
    if (!(n >= 1 && n <= 5000)) return;
    var key = n.toFixed(2);
    if (warmed[key]) return;
    warmed[key] = 'pending';

    fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Form-Secret': SECRET },
      body: JSON.stringify({ amount: key, ref: root.getAttribute('data-ref') || '' })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { warmed[key] = (d && d.variantId) || null; })
      .catch(function () { delete warmed[key]; });
  }

  function setBusyLabel(root, on) {
    var go = root && root.querySelector('[data-lol-submit]');
    if (!go) return;
    if (on) {
      if (!go.getAttribute('data-label')) go.setAttribute('data-label', go.textContent);
      go.textContent = 'Adding…';
    } else {
      go.textContent = go.getAttribute('data-label') || 'Add';
    }
  }

  function nameOrganisation(root) {
    if (!root || root.getAttribute('data-org-done') === '1') return;
    var ref = root.getAttribute('data-ref') || '';
    if (!ref) return;
    root.setAttribute('data-org-done', '1');

    function paint(name) {
      if (!name) return;
      Array.prototype.forEach.call(root.querySelectorAll('[data-lol-org]'), function (el) {
        el.textContent = name;
      });
    }
    if (orgCache[ref] !== undefined) return paint(orgCache[ref]);

    fetch(API + '?ref=' + encodeURIComponent(ref))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        orgCache[ref] = (d && d.organisation) || null;
        paint(orgCache[ref]);
      })
      .catch(function () {
        // Leave the generic wording. A name we could not confirm is worse than
        // no name on a sentence about where money goes.
        root.removeAttribute('data-org-done');
      });
  }

  function scan() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-lol-give]'), nameOrganisation);
  }

  /* ── Redrawing the cart ──────────────────────────────────────────────────
     Section Rendering keeps whoever is mid-purchase inside the drawer they
     opened. A reload is the fallback, not the default: reloading closes the
     drawer and reads as the click having failed. */
  function refreshCart() {
    fetch('/?sections=cart-drawer')
      .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
      .then(function (data) {
        var html = data['cart-drawer'];
        var current = document.querySelector('[data-cart-drawer]');
        if (!html || !current) return Promise.reject();
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer]');
        if (!fresh) return Promise.reject();
        current.replaceWith(fresh);
        busy = false;
        scan();
        document.dispatchEvent(new CustomEvent('cart:refreshed'));
      })
      .catch(function () { window.location.reload(); });
  }


  /* ── Adding a just-created variant ───────────────────────────────────────
     A variant minted seconds ago is not immediately sellable: the Admin API
     has it, the storefront has not caught up, and /cart/add.js answers 422
     "already sold out". Measured on this store — the same variant failed at
     0s and succeeded at 4s, with identical settings to the presets that work,
     so it is propagation and nothing else.

     That window is why "Other" appeared broken while $10/$20/$50 worked: those
     were created hours earlier. Retrying across it costs a few seconds on the
     first person to pick a given amount and nothing on everybody after, since
     the variant is then reused. */
  function addToCartWithRetry(variantId) {
    var waits = [0, 1200, 2500, 4000, 6000];
    var i = 0;

    function attempt() {
      return new Promise(function (resolve) { setTimeout(resolve, waits[i]); })
        .then(function () {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          });
        })
        .then(function (r) {
          if (r.ok) return true;
          i += 1;
          // Only "not there yet" is worth retrying. A 429 is Shopify throttling
          // and retrying harder makes it worse; anything else will not change.
          if (r.status === 422 && i < waits.length) return attempt();
          return r.text().then(function (t) {
            throw new Error(
              r.status === 429
                ? 'Too many attempts just now — please wait a moment and try again.'
                : t.indexOf('sold out') > -1
                  ? 'Still setting that amount up — please press Add once more.'
                  : 'We could not add that to your cart.'
            );
          });
        });
    }
    return attempt();
  }

  function add(root, amount) {
    if (busy) return;
    clearFail(root);
    lock(root, true);
    setBusyLabel(root, true);
    var ref = root.getAttribute('data-ref') || '';

    var key = Number(String(amount).replace(/[^0-9.]/g, ''));
    key = isFinite(key) ? key.toFixed(2) : '';
    var already = warmed[key];

    (already && already !== 'pending'
      ? Promise.resolve({ ok: true, d: { variantId: already } })
      : fetch(API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Form-Secret': SECRET },
          body: JSON.stringify({ amount: amount, ref: ref })
        }).then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
    )
      .then(function (res) {
        if (!res.ok || !res.d.variantId) {
          throw new Error(
            res.d && res.d.error === 'invalid_amount' && res.d.min
              ? 'Please enter an amount between $' + res.d.min + ' and $' + res.d.max + '.'
              : 'We could not add that just now.'
          );
        }
        return addToCartWithRetry(Number(res.d.variantId));
      })
      .then(function () { refreshCart(); })
      .catch(function (e) {
        lock(root, false);
        setBusyLabel(root, false);
        fail(root, (e && e.message) || 'Something went wrong. Please try again.');
      });
  }

  function removeGift(root) {
    var key = root.getAttribute('data-existing-key');
    if (!key || busy) return;
    lock(root, true);
    var body = { updates: {} };
    body.updates[key] = 0;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        if (!r.ok) throw new Error('remove failed');
        refreshCart();
      })
      .catch(function () {
        lock(root, false);
        fail(root, 'We could not remove that. Please try again.');
      });
  }

  /* ── One listener, for every drawer this page will ever have ─────────────── */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var amountBtn = t.closest('[data-lol-amount]');
    if (amountBtn) {
      e.preventDefault();
      return add(blockOf(amountBtn), Number(amountBtn.getAttribute('data-lol-amount')));
    }

    var otherBtn = t.closest('[data-lol-other]');
    if (otherBtn) {
      e.preventDefault();
      var root = blockOf(otherBtn);
      var custom = root && root.querySelector('[data-lol-custom]');
      if (custom) {
        custom.hidden = false;
        otherBtn.hidden = true;
        var input = root.querySelector('[data-lol-input]');
        if (input) input.focus();
      }
      return;
    }

    var go = t.closest('[data-lol-submit]');
    if (go) {
      e.preventDefault();
      var r1 = blockOf(go);
      var i1 = r1 && r1.querySelector('[data-lol-input]');
      return add(r1, i1 ? i1.value : '');
    }

    var rm = t.closest('[data-lol-remove]');
    if (rm) {
      e.preventDefault();
      return removeGift(blockOf(rm));
    }
  });


  document.addEventListener('input', function (e) {
    var input = e.target && e.target.closest && e.target.closest('[data-lol-input]');
    if (!input) return;
    var root = blockOf(input);
    if (!root) return;
    clearTimeout(warmTimer);
    warmTimer = setTimeout(function () { warm(root, input.value); }, 600);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var input = e.target && e.target.closest && e.target.closest('[data-lol-input]');
    if (!input) return;
    e.preventDefault();
    add(blockOf(input), input.value);
  });

  /* A drawer can appear long after this file ran, and more than once. */
  if (window.MutationObserver) {
    new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
})();
